import {BuildSessionV2, SessionV2} from './v2';
import {load} from './shortcode';

export enum APIVersion {
	v1 = 1,
	v2 = 2,
	v3 = 3
};

export type GetAuthFunc = (() => Promise<string | undefined>) | undefined

export type User = {
	session: ISession | null
	builds: ManagableSaveData[]
	id: string
}

export type Price = {
	amount: number
	currency: string
}


export type ImageContent = {
	image: string
}
export type VideoContent = {
	video: string
}
export type ExternalContent = {
	url: string
}
export type ContentType = (ImageContent|VideoContent|ExternalContent);

export enum ItemType {
	Group,
	Feature,
	Bundle,
	Kit,
	Accessory
}

export type Item = {
	id: string
	name: string
	categoryId?: string
	categoryName?: string
	description?: string
	rrp?: string
	rrpValue?: Price
	price?: string
	priceValue?: Price
	subtotalPrice?: string,
	subtotalPriceValue?: Price,
	icon?: string
	content?: ContentType[]
	locked?: number
	selected?: number
	minimum?: number
	maximum?: number
	breakdown?: {
		package: number
		kit: {[id: string]: number}
		bundle: {[id: string]: number}
		loose: number
	}
	deselectable?: boolean
	displayable?: boolean
	previewable?: boolean
	onAdd?: ((quantity?:number) => void)
	onRemove?: ((quantity?:number) => void)
	onClick?: (() => void)
	onAttach?: ((attachPoint?: string) => void)
	onDetach?: ((attachPoint?: string) => void)
	validAttachPoints?: string[]
	attachedPoints?: string[]
	analyticsName?: string
	validFrom?: string
	validTo?: string
	isNew?: boolean
	contains?: string[]
	outOfStock?: boolean
	itemType: ItemType
	tags?: string[]
	sortKey?: number
}

export enum Action {
	ChangedFeature = "changed_feature",
	AddedAccessory = "added_accessory",
	RemovedAccessory = "removed_accessory",
	RemovedKit = "removed_kit",
	RemovedBundle = "removed_bundle"
};

export const ZL_ACCESSORIES = "ZL_ACCESSORIES";
export const ZL_BUNDLES = "ZL_BUNDLES";
export const ZL_OPTIONS = "ZL_OPTIONS";

export type UpdateSummary = {
	action: Action
	item?: Item
	resolution?: ConflictResolution
}

export type ConflictResolution = {
	add: Item[]
	remove: Item[]
	splitBundles: Item[]
	unitsku: string|undefined
	getPrice: (financeParameters?:FinanceParameters) => FinanceDetails | undefined;
	getPriceDifference: (financeParameters?:FinanceParameters) => FinanceDetails | undefined;
	apply: () => void
}

export type ConflictDetails = {
	action: Action
	item: Item
	resolutions: ConflictResolution[]
}

export type AttachPointResolution = {
	id: string
	detach: Item | undefined
	attach: Item | undefined
	apply: () => void
}

export type AttachDetails = {
	item: Item
	resolutions: AttachPointResolution[]
}

export type SharingData = {
	title: string
	description: string
	url: string
	image?: string
	video?: string
} | undefined

export type OnBuildSessionUpdate = (buildSession: IBuildSession, summary?: UpdateSummary) => void
export type OnConflict = (details: ConflictDetails) => void
export type OnAttachChoice = (details: AttachDetails) => void
export type GetSharingData = (buildSession: IBuildSession) => SharingData
export type OnShortCode = (buildSession: IBuildSession, code: string) => void

export type FinanceParameters = {
	downpayment:number
	tradeIn: number
	months: number
	apr: number
}

export type FinanceDetails = {
	subtotal: string | undefined
	total: string | undefined
	monthly: string | undefined
	subtotalValue: number | undefined
	totalValue: number | undefined
	monthlyValue: number | undefined
	currency: string | undefined
}

export type SummaryDetails = {
	package: {
		id: string
		name: string
		features: string[]
		price: string | undefined
		rawprice: Price | undefined
		copy: string
		platformId: string
		modelYear: string
	}
	options: {
		groups: {
		id: string
		items: Item[]
		name: string | undefined
		subtotal: string | undefined
	}[]
		subtotal: string | undefined
		rawsubtotal: Price | undefined
	}
	accessories: {
		groups: {
			name: string
			items: Item[]
		}[]
		subtotal: string | undefined
		rawsubtotal: Price | undefined
	}
	fees: {
		subtotal: string | undefined
		items: {
			name: string
			price: string | undefined
		}[]
	} | undefined
	subtotalprice: string | undefined
	totalprice: string | undefined
	rawsubtotalprice: Price | undefined
	rawtotalprice: Price | undefined
	outOfStock: boolean | undefined
	prcodes: string[]
};

export type ShareTarget = "facebook"|"twitter"|"instagram"|"email";

//Used by 3rd party integrations, don't change the names or types of existing fields.
//Only use primative types to avoid hidden changes
export type SaveData = {
	uuid: string
	model: string
	year: string
	realplatform?: string
	package: string
	homologation: string
	unitsku: string 
	created: string
	modified: string
	locked: boolean
	expiry: string
	locale: string
	region: string
	name: string
	price: {
		amount: number,
		currency: string,
		subtotal?: {
			amount: number,
			currency: string
		}
		total?: {
			amount: number,
			currency: string
		}
		hide?: boolean
	}
	fees?: {
		[type: string]: {amount: number, currency: string}
	}
	selected: {
		featureid?: string
		accessorysku?: string
		category: string
		locked: number
		quantity: number
		attached?: string[]
		addedautomatically?: boolean
	}[]
	segment: string
	shortcode: string
	autosaved?: boolean
}

export type ManagableSaveData = {
	delete: () => void
	isPreorderOnly: () => boolean
	preorder: () => Promise<void>
	requestQuote: () => Promise<void>
	share: (channel:ShareTarget, image: string, lock: boolean) => void
	getPlatformId: (real?: boolean) => string
	getPackageId: () => string
	getModelSku: () => string
	getModelYear: () => string
	getPRCodes: (stripIncludedAccessories?: boolean) => string[]
	
} & SaveData

export type Without<T> = { [P in keyof T]?: undefined };
export type XOR<T, U> = (Without<T> & U) | (Without<U> & T)
export type LogicAnd = {and?:(Logic | string)[]}
export type LogicOr = {or?: (Logic | string)[]}
export type LogicXor ={xor?: (Logic | string)[]}
export type LogicNot =  {not?: (Logic | string)}
export type Logic = XOR<LogicAnd, XOR<LogicOr, XOR<LogicXor, LogicNot>>> | string;
export type Blacklist = (string | {code: string, country?: string[]} & Logic)[];

export const processLogic = (codes: string[], logic: Logic | undefined) : boolean => {
	if(!logic) {
		return false;
	}
	const anylogic = logic as any;
	if(anylogic.not) {
		return !processLogic(codes, anylogic.not);
	}
	else if(anylogic.xor) {
		return anylogic.xor.reduce((count: number, l:any) => count + (processLogic(codes, l) ? 0 : 1), 0) === 1;
	}
	else if(anylogic.or) {
		return anylogic.or.some((l:any) => processLogic(codes, l));
	}
	else if(anylogic.and) {
		return anylogic.and.every((l:any) => processLogic(codes, l));
	}
	if(typeof(logic) === "string") {
		return codes.map(c => c.toLowerCase()).includes(logic.toLowerCase());
	}
	return true;
}

export const formatPrice = (price: Price | undefined, locale: string, negate?: boolean) : string | undefined => {
	if(!price || !price.currency) {
		return undefined;
	}
	let currency = price.currency;

	//Workaround for dodgy browser handling of Norwegian Krone [BRPLA-78]
	if(price.currency === "NOK") {
		currency = "SEK";
		locale = "sv-SE";
	}
	
	try {
		return new Intl.NumberFormat(locale, {style: 'currency', currency}).format((negate ? -1 : 1) * price.amount);
	}
	catch {
		console.error("Failed to format price", {price, locale, currency})
	}
	return undefined
}

export interface IBuildSession {
	readonly session: ISession;
	readonly state: any;
	readonly onBuildSessionUpdate: OnBuildSessionUpdate;
	readonly onConflict: OnConflict;
	readonly onAttachChoice: OnAttachChoice;
	readonly onShortCode: OnShortCode;
	
	start(initialState: Object, testDataSource: any) : Promise<IBuildSession>;
	clone(onBuildSessionUpdate: OnBuildSessionUpdate, onConflict: OnConflict, onAttachChoice: OnAttachChoice, onShortCode: OnShortCode, lock: boolean) : IBuildSession;
	
	close() : void;

	forceRequestShortCode() : void;
	getShortCode() : Promise<string>;

	save() : Promise<ManagableSaveData>;
	
	doExport() : any
	doImport(data: any) : void;

	hasUnsavedChanges() : boolean;
	locked() : boolean;

	retailSave(message: string) : Promise<void>
	changePackage() : void;
	getFinance() : Promise<void>;

	/**
	 * @deprecated relies on environment
	 */
	isPreorderOnly() : boolean;

	requestQuote() : Promise<void>;
	preorder() : Promise<void>;
	promotion() : Promise<void>;
	share(channel:ShareTarget, image: string, lock: boolean): Promise<void>;

	resetUnit() : void; //Resets the current build to stock for the current unit (all non-included accessories removed, options such as colour & engine are unchanged)
	resetBuild() : void; //Resets the current build to what was originally opened (including any desiredAccessories) as long as the initial data exists, otherwise the same as resetUnit

	setConfig(blacklist: Blacklist, priorityList: string[], ignoreAttachPoints?: boolean | string[], optimise?: boolean, inject?: {[id: string]: any}, hidePrices?: boolean, autosave?: boolean) : void;

	getProductLineUp() : string;
	getCountryCode() : string;

	getModelType() : string;
	getModelRangeName(isOverriden?: boolean) : string;
	getModelName() : string;
	getModelSegment() : string;
	getMarketingName() : string;
	getDefaultMarketingImage() : string;
	getPlatformId(real?: boolean) : string;
	getPackageId() : string;
	getModelSku() : string;
	getModelYear(isOverriden?: boolean) : string; //TODO: Remove isOverriden option, its misused and poorly defaulted
	getPRCodes(stripIncludedAccessories?: boolean) : string[];
	getColorCode() : string;
	getHomologationId() : string;
	getFeatureCode(featureCategory: string, nosanitise?: boolean) : string;
	getEngineBracket(id: string): number;
	getMarketingSummary(): string;
	getModifiedAt() : number;
	isOutOfStock() : boolean;
	
	getOption(id: string) : Item;
	getAllOptions() : Item[];

	getOptions(parentGroupId?:string, maxTopLevelItems?: number, userFilter?: (item:Item) => boolean, userFilterCollapse?: number) : Item[];

	getPricingInfo(financeParameters?: FinanceParameters, add?: Item[], remove?: Item[], unitId?: string) : FinanceDetails | undefined;
	getApplicableFee(type: string) : Price | null;

	getSummary(includeIncluded?: boolean) : SummaryDetails;
	
	sanitizeFeatureCode(featureCategory: string, code: string) : string;

	getTypeForItemId(id: string) : string | undefined;
	getCategoryForItemId(id: string) : string[] | undefined;

	getOptimisationLevel() : number;
}

let users: { [key: string]: User } = {};
 
export interface ISession {
	version: number;
	id: string;
	userId: string;
	server: string;
	brand: string;
	locale: string;
	zlservice: string;

	requestBuilds() : Promise<ManagableSaveData[]>;
}

function _getSession(userId: string){
	if (users[userId] !== undefined){
		if (users[userId].session) {
			return users[userId].session;
		}
	}
	return null;
}

export function StartSession(version: number, userId: string, server: string, brand: string, locale: string, region: string, currency: string, zlservice: string = "stage", authFunc: GetAuthFunc = undefined){
	switch(version) {
		default:
			users[userId] = {id: userId, session: new SessionV2(userId, server, brand, locale.slice(3), locale, region, currency, zlservice, authFunc), builds: []}
			break;
	}
	return users[userId].session!.id;
}

export function GetUser(userId: string){
	return users[userId];
}

export function GetBuilds(userId: string) : Promise<ManagableSaveData[]> {
	return new Promise<ManagableSaveData[]>(async (resolve, reject) => {
		let session = _getSession(userId);
		if(session) {
			users[userId].builds = await session.requestBuilds();
			resolve(users[userId].builds);
		}
		else {
			reject("Was unable to request builds - no active session for " + userId)
		}
	});
}

export function StartBuildSession(userId: string, buildId: string, onBuildSessionUpdate?: OnBuildSessionUpdate, onConflict?: OnConflict, onAttachChoice?: OnAttachChoice, getSharingData?: GetSharingData, onShortCode?: OnShortCode, initialData?: any, shortcode?: string, testDataSource?: any) : Promise<IBuildSession>{
	
	return new Promise<IBuildSession>(async (resolve, reject) => { 
		let user = users[userId];
		if(user && user.session) {
			let build = undefined;
			if(shortcode) {
				build = (await load(shortcode, user.session.zlservice)).state;
				if(!build || build.error) {
					reject("Failed to load shortcode")
					return;
				}
			}
			else {
				build = user.builds.find(b => b.uuid === buildId);
			}
			if(build) {
				initialData = build;

				/* shouldn't need this if we match up the naming conventions but right now the 
				field names we store don't match what we're using here for setup. */
				if (!initialData.packageId && build.package){
					initialData.packageId = build.package;
				}

				if (!initialData.platformId && build.model){
					initialData.platformId = build.model;
				}

				if (!initialData.modelYear && build.year){
					initialData.modelYear = build.year;
				}
				/* ------------------------------------------------------------------------------- */
			}

			if(initialData) {
				let data = {};
				let buildSession : IBuildSession | null = null;
				switch(user.session.version) {
					case APIVersion.v2:
						data = {
							buildId,
							model: initialData.platformId,
							pkg: initialData.packageId,
							year: initialData.modelYear,
							unitsku: initialData.unitId,
							requiredFeatures: initialData.requiredFeatures,
							desiredFeatures: initialData.desiredFeatures,
							desiredAccessories: initialData.desiredAccessories
						};

						buildSession = new BuildSessionV2(user.session, {}, build || {}, onBuildSessionUpdate, onConflict, onAttachChoice, getSharingData, onShortCode, undefined, undefined, undefined, true, shortcode);
						break;
				}

				if(buildSession) {
					try {
						buildSession = await buildSession.start(data, testDataSource);
						resolve(buildSession);
						return;
					}
					catch(err){
						reject(err);
						return;
					}
				}
				reject("Failed to create build session");
				return;
			}
			reject("Missing initialisation data")
			return
		}
		reject("Session not found")
	});
}
